import {
  OrganizationSettingsDocument,
  OrganizationSettingsQuery,
  useOrganizationRemoveLanguageMutation,
} from 'lib-knowledge-management-common/graphql';
import { LanguageModel } from 'lib-knowledge-management-common/utils/language';

type MutationFunction = ReturnType<
  typeof useOrganizationRemoveLanguageMutation
>[0];
type MutationReturnValue = ReturnType<MutationFunction>;
type LanguageRemoveFn = (language: LanguageModel) => MutationReturnValue;

type UseLanguageRemoveReturn = [
  LanguageRemoveFn,
  ReturnType<typeof useOrganizationRemoveLanguageMutation>[1],
];

type InputOptions = {
  onCompleted?: () => void;
};

export function useLanguageRemove({
  onCompleted,
}: InputOptions = {}): UseLanguageRemoveReturn {
  const [removeLanguageFn, mutationResult] =
    useOrganizationRemoveLanguageMutation();

  const onRemoveLanguage: LanguageRemoveFn = async (
    language: LanguageModel,
  ) => {
    return removeLanguageFn({
      variables: {
        languageId: language.languageId,
      },
      update: (cache) => {
        const currentOrgSettings = cache.readQuery<OrganizationSettingsQuery>({
          query: OrganizationSettingsDocument,
        });

        if (currentOrgSettings) {
          const updatedEnabledLanguages =
            currentOrgSettings.organisationSettings.enabledLanguages.filter(
              (lang) => lang.languageId !== language.languageId,
            );

          cache.writeQuery<OrganizationSettingsQuery>({
            query: OrganizationSettingsDocument,
            data: {
              ...currentOrgSettings,
              organisationSettings: {
                ...currentOrgSettings.organisationSettings,
                enabledLanguages: updatedEnabledLanguages,
              },
            },
          });
        }
      },
      onCompleted,
    });
  };

  return [onRemoveLanguage, mutationResult];
}
